/**
 * Pure aggregation helpers for the site data-flow transform.
 *
 * - `aggregateRecipientRows` reduces every payload-bearing edge into the
 *   recipients-table row shape.
 * - `buildLinksWithCollapse` enforces the Sankey edge cap by folding
 *   low-weight tail edges under an "other-sources" pseudo-node.
 * - `buildPageConsentModes` reverses the page-side edges to recover each
 *   page's consent mode for label suffixing.
 *
 * No DOM, no D3 — every export is a unit-testable pure function.
 */

import type { components } from '@trackercop/contracts';
import {
  PAYLOAD_CATEGORY_SEVERITY,
  dominantCategoryOf,
} from './payload-category-palette';

type DataFlowSummary = components['schemas']['DataFlowSummary'];
type DataFlowVendor = components['schemas']['DataFlowVendor'];
type DataFlowVendorNode = components['schemas']['DataFlowVendorNode'];
type DataFlowRecipient = components['schemas']['DataFlowRecipient'];
type DataFlowPage = components['schemas']['DataFlowPage'];
type DataFlowEdge = components['schemas']['DataFlowEdge'];
type PayloadCategory = components['schemas']['PayloadCategory'];
type ConsentMode = components['schemas']['ConsentMode'];

export const RECIPIENT_PREFIX = 'recipient:';
export const OTHER_SOURCES_ID = 'other-sources:__collapsed__';

export interface NodeIndex {
  pagesById: Map<string, DataFlowPage>;
  vendorsById: Map<string, DataFlowVendorNode>;
  recipientsById: Map<string, DataFlowRecipient>;
  /** All-up id -> column-bucket mapping for sankey layout. */
  columnOf: Map<string, number>;
}

export interface RecipientTableRow {
  recipientNodeId: string;
  vendor: DataFlowVendor;
  /** Total payload count across every chain feeding this recipient. */
  totalRequests: number;
  /** Number of distinct pages that sent something to this recipient. */
  pagesContacted: number;
  /** Categories observed on any inbound edge to this recipient. */
  categories: ReadonlySet<PayloadCategory>;
  /** Pre-computed for "sort by dominant" without re-summing every render. */
  dominantCategory: PayloadCategory;
  /** Consent modes observed on any inbound edge to this recipient. */
  consentModes: ReadonlySet<ConsentMode>;
}

export interface SiteDataFlowSankeyLink {
  sourceId: string;
  targetId: string;
  weight: number;
  dominantCategory: PayloadCategory;
  categories: ReadonlySet<PayloadCategory>;
  consentModes: ReadonlySet<ConsentMode>;
  /**
   * True when this edge terminates at a cloaked recipient — the
   * Sankey renderer draws cloaked edges with a dashed stroke so a
   * reviewer can spot first-party CNAME-cloaked vendor traffic at
   * a glance even when the screenshot is small.
   */
  cloaked: boolean;
}

export interface CollapseResult {
  links: SiteDataFlowSankeyLink[];
  /** Pseudo-node returned when fold occurred; null otherwise. */
  otherSourcesNode: {
    id: string;
    label: string;
    /** Stays at column 1 — the fold is a synthetic source on the page side. */
    column: 1;
  } | null;
  collapsedEdgeCount: number;
}

export function indexNodes(summary: DataFlowSummary): NodeIndex {
  const pagesById = new Map<string, DataFlowPage>();
  for (const p of summary.pages) pagesById.set(p.nodeId, p);
  const vendorsById = new Map<string, DataFlowVendorNode>();
  for (const v of summary.vendors) vendorsById.set(v.nodeId, v);
  const recipientsById = new Map<string, DataFlowRecipient>();
  for (const r of summary.recipients) recipientsById.set(r.nodeId, r);

  const maxVendorDepth = summary.vendors.reduce(
    (acc, v) => (v.depthBucket > acc ? v.depthBucket : acc),
    0,
  );

  const columnOf = new Map<string, number>();
  for (const p of summary.pages) columnOf.set(p.nodeId, 0);
  for (const v of summary.vendors) columnOf.set(v.nodeId, 1 + v.depthBucket);
  // Recipients sit at maxVendorDepth + 2 (+1 for the page column, +1 to
  // sit one column to the right of the deepest vendor).
  const recipientColumn = 1 + maxVendorDepth + 1;
  for (const r of summary.recipients) columnOf.set(r.nodeId, recipientColumn);

  return { pagesById, vendorsById, recipientsById, columnOf };
}

export function aggregateRecipientRows(
  summary: DataFlowSummary,
  index: NodeIndex,
): RecipientTableRow[] {
  const acc = new Map<
    string,
    {
      vendor: DataFlowVendor;
      totalRequests: number;
      pages: Set<string>;
      categories: Set<PayloadCategory>;
      consentModes: Set<ConsentMode>;
    }
  >();

  // Build a reverse adjacency so we can ascend from a recipient back to
  // page nodes by repeatedly walking edges in reverse.
  const reverseAdj = new Map<string, DataFlowEdge[]>();
  for (const e of summary.flows) {
    const list = reverseAdj.get(e.targetId);
    if (list) list.push(e);
    else reverseAdj.set(e.targetId, [e]);
  }

  function pagesReaching(nodeId: string): Set<string> {
    const seen = new Set<string>();
    const pages = new Set<string>();
    const stack: string[] = [nodeId];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      const inbound = reverseAdj.get(cur) ?? [];
      for (const e of inbound) {
        if (index.pagesById.has(e.sourceId)) pages.add(e.sourceId);
        else stack.push(e.sourceId);
      }
    }
    return pages;
  }

  for (const edge of summary.flows) {
    if (!edge.targetId.startsWith(RECIPIENT_PREFIX)) continue;
    const recipient = index.recipientsById.get(edge.targetId);
    if (!recipient) continue;

    let row = acc.get(edge.targetId);
    if (!row) {
      row = {
        vendor: recipient.vendor,
        totalRequests: 0,
        pages: pagesReaching(edge.targetId),
        categories: new Set<PayloadCategory>(),
        consentModes: new Set<ConsentMode>(),
      };
      acc.set(edge.targetId, row);
    }
    row.totalRequests += edge.weight;
    for (const c of edge.categories) row.categories.add(c);
    for (const m of edge.consentModes) row.consentModes.add(m);
  }

  const rows: RecipientTableRow[] = [];
  for (const [recipientNodeId, row] of acc) {
    rows.push({
      recipientNodeId,
      vendor: row.vendor,
      totalRequests: row.totalRequests,
      pagesContacted: row.pages.size,
      categories: row.categories,
      dominantCategory: dominantCategoryOf(row.categories),
      consentModes: row.consentModes,
    });
  }

  rows.sort(
    (a, b) =>
      b.totalRequests - a.totalRequests ||
      PAYLOAD_CATEGORY_SEVERITY[b.dominantCategory] -
        PAYLOAD_CATEGORY_SEVERITY[a.dominantCategory] ||
      a.vendor.displayName.localeCompare(b.vendor.displayName),
  );
  return rows;
}

export function buildPageConsentModes(
  summary: DataFlowSummary,
  index: NodeIndex,
): Map<string, Set<ConsentMode>> {
  const out = new Map<string, Set<ConsentMode>>();
  for (const e of summary.flows) {
    if (!index.pagesById.has(e.sourceId)) continue;
    let acc = out.get(e.sourceId);
    if (!acc) {
      acc = new Set<ConsentMode>();
      out.set(e.sourceId, acc);
    }
    for (const m of e.consentModes) acc.add(m);
  }
  return out;
}

export function buildLinksWithCollapse(
  flows: readonly DataFlowEdge[],
  maxEdges: number,
  cloakedRecipientIds: ReadonlySet<string>,
): CollapseResult {
  const baseLinks: SiteDataFlowSankeyLink[] = flows.map((e) => ({
    sourceId: e.sourceId,
    targetId: e.targetId,
    weight: Math.max(1, e.weight),
    dominantCategory: e.dominantCategory,
    categories: new Set(e.categories),
    consentModes: new Set(e.consentModes),
    cloaked: cloakedRecipientIds.has(e.targetId),
  }));

  if (baseLinks.length <= maxEdges) {
    return { links: baseLinks, otherSourcesNode: null, collapsedEdgeCount: 0 };
  }

  const sorted = [...baseLinks].sort((a, b) => b.weight - a.weight);
  const keep = sorted.slice(0, maxEdges - 1);
  const fold = sorted.slice(maxEdges - 1);
  if (fold.length === 0) {
    return { links: baseLinks, otherSourcesNode: null, collapsedEdgeCount: 0 };
  }

  const perTarget = new Map<
    string,
    { weight: number; cats: Set<PayloadCategory>; modes: Set<ConsentMode> }
  >();
  for (const e of fold) {
    const existing = perTarget.get(e.targetId);
    if (existing) {
      existing.weight += e.weight;
      for (const c of e.categories) existing.cats.add(c);
      for (const m of e.consentModes) existing.modes.add(m);
    } else {
      perTarget.set(e.targetId, {
        weight: e.weight,
        cats: new Set(e.categories),
        modes: new Set(e.consentModes),
      });
    }
  }

  const folded: SiteDataFlowSankeyLink[] = [];
  for (const [targetId, acc] of perTarget) {
    folded.push({
      sourceId: OTHER_SOURCES_ID,
      targetId,
      weight: acc.weight,
      dominantCategory: dominantCategoryOf(acc.cats),
      categories: acc.cats,
      consentModes: acc.modes,
      cloaked: cloakedRecipientIds.has(targetId),
    });
  }

  return {
    links: [...keep, ...folded],
    otherSourcesNode: {
      id: OTHER_SOURCES_ID,
      label: `${fold.length} other sources (collapsed)`,
      column: 1,
    },
    collapsedEdgeCount: fold.length,
  };
}

